import React, { useEffect, useState } from "react";

import { FaEnvelope, FaInstagram, FaLinkedin } from "react-icons/fa";
import { Link } from "react-router-dom";

import AboutMeSection from "./AboutMeSection";
import FAQSection from "./FAQSection";
import Header from "./Header";
import HeroSection from "./HeroSection";
import IndiaMapSection from "./IndiaMapSection";
import SignupForm from "./SignupForm";
import TestimonialsSection from "./TestimonialsSection";
import WhyCohopSection from "./WhyCohopSection";
import { initGA, trackPageView, trackSectionView } from "../utils/analytics";

interface QuickLink {
  id: string;
  label: string;
}

const quickLinks: QuickLink[] = [
  { id: "hero", label: "Home" },
  { id: "why-cohop", label: "Why Cohop" },
  { id: "cities", label: "Cities" },
  { id: "testimonials", label: "Testimonials" },
  { id: "faq", label: "FAQ" },
  { id: "about-me", label: "About" },
];

/**
 * Scrolls to a section element with proper offset to account for fixed header
 * @param sectionId - The ID of the section element to scroll to
 * @param headerHeight - The height of the fixed header (default: 72px)
 */
export const scrollToSection = (sectionId: string, headerHeight = 72): void => {
  const element = document.getElementById(sectionId);
  if (element) {
    const elementPosition = element.offsetTop - headerHeight;
    window.scrollTo({ top: elementPosition, behavior: "smooth" });
  }
};

const LandingPage: React.FC = () => {
  const [isViewingHeroSection, setIsViewingHeroSection] =
    useState<boolean>(false);
  const [isViewingSignupForm, setIsViewingSignupForm] =
    useState<boolean>(false);

  useEffect(() => {
    // Initialize Google Analytics
    initGA();
    trackPageView(window.location.href, document.title);

    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    document.documentElement.style.scrollBehavior = "smooth";

    // Track sections that have been viewed
    const viewedSections = new Set<string>();

    const handleScrollDetection = (): void => {
      const HEADER_HEIGHT = 64;

      // Update specific section states for header
      const heroElement = document.getElementById("hero");
      if (heroElement) {
        const heroRect = heroElement.getBoundingClientRect();
        const isHeroVisible =
          heroRect.top <= HEADER_HEIGHT && heroRect.bottom > HEADER_HEIGHT;
        setIsViewingHeroSection(isHeroVisible);

        // Track hero section view
        if (isHeroVisible && !viewedSections.has("hero")) {
          viewedSections.add("hero");
          trackSectionView("hero_section");
        }
      }

      const signupFormElement = document.getElementById("signup-form");
      if (signupFormElement) {
        const signupFormRect = signupFormElement.getBoundingClientRect();
        const isSignupFormVisible =
          signupFormRect.top <= HEADER_HEIGHT &&
          signupFormRect.bottom > HEADER_HEIGHT;
        setIsViewingSignupForm(isSignupFormVisible);

        // Track signup form section view
        if (isSignupFormVisible && !viewedSections.has("signup-form")) {
          viewedSections.add("signup-form");
          trackSectionView("signup_form_section");
        }
      }

      // Track other sections
      const sections = ["about-me", "why-cohop", "testimonials", "faq"];
      sections.forEach((sectionId) => {
        const element = document.getElementById(sectionId);
        if (element && !viewedSections.has(sectionId)) {
          const rect = element.getBoundingClientRect();
          const isVisible =
            rect.top <= window.innerHeight * 0.5 &&
            rect.bottom >= window.innerHeight * 0.5;
          if (isVisible) {
            viewedSections.add(sectionId);
            trackSectionView(sectionId.replace("-", "_") + "_section");
          }
        }
      });
    };

    window.addEventListener("scroll", handleScrollDetection);

    // Call once on mount to set initial state
    handleScrollDetection();

    return () => {
      window.removeEventListener("scroll", handleScrollDetection);
    };
  }, []);

  const handleQuickLinkClick = (sectionId: string): void => {
    scrollToSection(sectionId);
  };

  return (
    <div className="font-sans text-gray-800 bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 min-h-screen w-full max-w-full overflow-x-hidden">
      <Header
        isViewingHeroSection={isViewingHeroSection}
        isViewingSignupForm={isViewingSignupForm}
      />

      <main>
        <HeroSection />
        <WhyCohopSection />
        <IndiaMapSection />
        <TestimonialsSection />
        <FAQSection />
        <SignupForm />
        <AboutMeSection />
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-pink-100 to-purple-100 border-t border-pink-200">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="space-y-4 lg:col-span-2 text-center md:text-left">
              <img
                src="/logo.svg"
                alt="Cohop Logo"
                className="h-16 w-auto mx-auto md:mx-0"
              />
              <p className="text-gray-600 text-sm leading-relaxed">
                Travel. Explore. Safer. Together.
              </p>
              <div className="flex space-x-3 justify-center md:justify-start">
                <a
                  href="mailto:hello@cohop.in"
                  className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center hover:bg-purple-600 transition-colors duration-200"
                  aria-label="Email"
                >
                  <FaEnvelope className="w-4 h-4" />
                </a>
                <a
                  href="https://www.instagram.com/cohop.india/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full flex items-center justify-center hover:from-pink-600 hover:to-purple-600 transition-all duration-200"
                  aria-label="Instagram"
                >
                  <FaInstagram className="w-4 h-4" />
                </a>
                <a
                  href="https://www.linkedin.com/company/cohop-ridesharing/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors duration-200"
                  aria-label="LinkedIn"
                >
                  <FaLinkedin className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="space-y-4 text-center md:text-left">
              <h4 className="text-lg font-semibold text-purple-800">
                Quick Links
              </h4>
              <ul className="space-y-2 text-sm">
                {quickLinks.map((link) => (
                  <li key={link.id}>
                    <button
                      onClick={() => handleQuickLinkClick(link.id)}
                      className="text-gray-600 hover:text-purple-600 transition-colors duration-200"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div className="space-y-4 text-center md:text-left">
              <h4 className="text-lg font-semibold text-purple-800">
                Contact and Info
              </h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>
                  <a
                    href="mailto:hello@cohop.in"
                    className="hover:text-purple-600 transition-colors duration-200 flex items-center gap-2 justify-center md:justify-start"
                  >
                    <FaEnvelope className="w-4 h-4" />
                    hello@cohop.in
                  </a>
                </li>
                <li>
                  <Link
                    to="/privacy-policy"
                    onClick={() => window.scrollTo(0, 0)}
                    className="text-gray-600 hover:text-purple-600 transition-colors duration-200"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <span className="text-gray-600">Women-Only Service</span>
                  <span className="text-xs text-green-600 block">
                    ✓ Verified
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-pink-200 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-600 text-center md:text-left">
              &copy; {new Date().getFullYear()} Cohop. All rights reserved. Made
              with 💜 for women travelers.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
